/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Select, Card, Row, Col, message, Tag, Divider } from 'antd';
import { imagingOrderService, imagingResultService, imagingStudyService, imagingDepartmentService } from '../../../services/imagingService';
import { doctorService } from '../../../services/doctorService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const { TextArea } = Input;

const ImagingResultForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [result, setResult] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form] = Form.useForm();

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await doctorService.getAll({ per_page: 100 });
      setDoctors(response.data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  }, []);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    try {
      const orderData = await imagingOrderService.getById(parseInt(id));
      setOrder(orderData);

      if (orderData.result) {
        setResult(orderData.result);
        form.setFieldsValue({
          radiologist_id: orderData.result.radiologist_id,
          template_id: orderData.result.template_id,
          findings: orderData.result.findings,
          impression: orderData.result.impression,
          recommendations: orderData.result.recommendations,
          bi_rads_category: orderData.result.bi_rads_category,
          report_text: orderData.result.report_text,
          status: orderData.result.status,
        });
      }

      if (orderData.study?.id) {
        const templateData = await imagingStudyService.getTemplates(orderData.study.id);
        setTemplates(templateData);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      message.error('Error al cargar la orden');
      navigate('/imaging');
    }
  }, [id, navigate, form]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      if (!form.getFieldValue('findings')) {
        form.setFieldValue('findings', template.findings_template);
      }
      if (!form.getFieldValue('impression')) {
        form.setFieldValue('impression', template.impression_template);
      }
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (result) {
        await imagingResultService.update(result.id, values);
        message.success('Resultado actualizado correctamente');
      } else {
        await imagingResultService.createResult(parseInt(id), values);
        message.success('Resultado guardado correctamente');
      }
      navigate('/imaging');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al guardar resultado';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const colors = { draft: 'default', final: 'blue', amended: 'orange', verified: 'green' };
    const labels = { draft: 'Borrador', final: 'Final', amended: 'Enmienda', verified: 'Verificado' };
    return <Tag color={colors[status]}>{labels[status]}</Tag>;
  };

  if (!order) {
    return <div className="text-center py-5">Cargando...</div>;
  }

  return (
    <>
      <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Resultado de Imagen</h3>
              <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Órdenes', to: '/imaging' }, { label: 'Resultado' }]} />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <Card title="Información de la Orden">
              <p><strong>Paciente:</strong> {order.patient ? `${order.patient.first_name} ${order.patient.last_name}` : 'N/A'}</p>
              <p><strong>Estudio:</strong> {order.study?.name}</p>
              <p><strong>Departamento:</strong> {order.study?.department?.name}</p>
              <p><strong>Médico Solicitante:</strong> {order.doctor ? `${order.doctor.first_name} ${order.doctor.last_name}` : 'N/A'}</p>
              <p><strong>Indicación Clínica:</strong> {order.clinical_indication || 'N/A'}</p>
              <Divider />
              <p><strong>Estado de Orden:</strong> {getStatusTag(order.status)}</p>
              <p><strong>Fecha:</strong> {order.ordered_at ? new Date(order.ordered_at).toLocaleString() : 'N/A'}</p>
            </Card>
          </div>

          <div className="col-md-8">
            <Card title={result ? 'Editar Resultado' : 'Nuevo Resultado'}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                  status: result?.status || 'draft',
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Radiólogo"
                      name="radiologist_id"
                      rules={[{ required: true, message: 'El radiólogo es requerido' }]}
                    >
                      <Select
                        placeholder="Seleccione radiólogo"
                        showSearch
                        optionFilterProp="children"
                      >
                        {doctors.map(d => (
                          <Select.Option key={d.id} value={d.id}>
                            {d.first_name} {d.last_name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Plantilla"
                      name="template_id"
                    >
                      <Select
                        placeholder="Seleccione plantilla"
                        onChange={handleTemplateSelect}
                        allowClear
                      >
                        {templates.map(t => (
                          <Select.Option key={t.id} value={t.id}>
                            {t.name} {t.is_default ? '(Por defecto)' : ''}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Hallazgos (Findings)"
                  name="findings"
                >
                  <TextArea rows={6} placeholder="Describa los hallazgos del estudio..." />
                </Form.Item>

                <Form.Item
                  label="Impresión Diagnóstica"
                  name="impression"
                >
                  <TextArea rows={4} placeholder="Impresión o conclusión diagnóstica..." />
                </Form.Item>

                <Form.Item
                  label="Recomendaciones"
                  name="recommendations"
                >
                  <TextArea rows={3} placeholder="Recomendaciones si aplica..." />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Categoría BI-RADS"
                      name="bi_rads_category"
                    >
                      <Select placeholder="Seleccione categoría" allowClear>
                        <Select.Option value="BI-RADS 0">BI-RADS 0 - Necesita evaluación adicional</Select.Option>
                        <Select.Option value="BI-RADS 1">BI-RADS 1 - Negativo</Select.Option>
                        <Select.Option value="BI-RADS 2">BI-RADS 2 - Hallazgos benignos</Select.Option>
                        <Select.Option value="BI-RADS 3">BI-RADS 3 - Probablemente benigno</Select.Option>
                        <Select.Option value="BI-RADS 4">BI-RADS 4 - Sospechoso</Select.Option>
                        <Select.Option value="BI-RADS 5">BI-RADS 5 - Altamente sospechoso de malignidad</Select.Option>
                        <Select.Option value="BI-RADS 6">BI-RADS 6 - Malignidad conocida</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Estado del Informe"
                      name="status"
                    >
                      <Select placeholder="Seleccione estado">
                        <Select.Option value="draft">Borrador</Select.Option>
                        <Select.Option value="final">Final</Select.Option>
                        <Select.Option value="amended">Enmienda</Select.Option>
                        <Select.Option value="verified">Verificado</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Informe Completo"
                  name="report_text"
                >
                  <TextArea rows={4} placeholder="Texto libre del informe..." />
                </Form.Item>

                <Form.Item className="text-end">
                  <Button onClick={() => navigate('/imaging')} className="me-2">
                    Cancelar
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {result ? 'Actualizar' : 'Guardar Resultado'}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
    </>
  );
};

export default ImagingResultForm;
